import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { Button, ErrorText, Flex, FormField, Modal, TextField } from '@cmaas/ui';
import type { Tenant } from '../../api/types';
import { apiFetch, CmaasApiError } from '../../api/client';

// The tenant name is the Apple UAP issuer organization, which Apple caps at 64 characters.
const editSchema = z.object({ name: z.string().trim().min(1).max(64) });
type EditValues = z.infer<typeof editSchema>;

const mutationError = (err: unknown) => (err instanceof CmaasApiError ? err.message : 'Something went wrong.');

export function EditTenantModal({
  tenant,
  open,
  onOpenChange,
}: {
  tenant: Tenant;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const queryClient = useQueryClient();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<EditValues>({ resolver: zodResolver(editSchema), defaultValues: { name: tenant.name } });

  // Re-seed the form when the modal opens or the tenant refetches, so it never shows a stale name.
  useEffect(() => {
    if (open) reset({ name: tenant.name });
  }, [open, tenant.name, reset]);

  const update = useMutation({
    mutationFn: (values: EditValues) =>
      apiFetch<Tenant>(`/v1/admin/tenants/${tenant.id}`, { method: 'PATCH', body: values }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['tenant', tenant.id] });
      void queryClient.invalidateQueries({ queryKey: ['tenants'] });
      void queryClient.invalidateQueries({ queryKey: ['tenant-options'] });
      onOpenChange(false);
    },
  });

  const handleOpenChange = (next: boolean) => {
    if (!next) update.reset();
    onOpenChange(next);
  };

  return (
    <Modal open={open} onOpenChange={handleOpenChange} title="Edit tenant">
      <form onSubmit={handleSubmit((v) => update.mutate(v))}>
        <Flex direction="column" gap="3" mt="4">
          <FormField label="Name" error={errors.name?.message}>
            <TextField.Root {...register('name')} />
          </FormField>
          {update.isError && <ErrorText>{mutationError(update.error)}</ErrorText>}
          <Flex gap="3" justify="end" mt="2">
            <Button type="button" variant="soft" onClick={() => handleOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={update.isPending}>
              Save
            </Button>
          </Flex>
        </Flex>
      </form>
    </Modal>
  );
}
